import  { useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./SubHeader.scss";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../ContextStore/ThemeContext";
import { routerSelector } from "../../store/routerSlice";
import { useSelector } from "react-redux";
import { NextWeek, KeyboardArrowRight, ExpandMore } from "@mui/icons-material";
import { Button, Menu, MenuItem } from "@mui/material";
import ScheduleReportModal from "../ScheduleReportModal";

const formatKey = key => {
  return key.split(" ").join("-").toLowerCase();
};

const ReportItemSubMenuItem = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const { filters } = menu;
  return (
    <>
      <MenuItem onClick={() => setOpen(true)} key={formatKey(menu.title)} sx={{ width: "100%" }}>
        {menu.title}
      </MenuItem>
      <ScheduleReportModal
        isModalOpen={open}
        setIsModalOpen={setOpen}
        showScheduleSection={false}
        reportLink={`/${menu.link}`}
        fileName={menu.fileName}
        filters={filters}
        buttonText={"Open"}
      />
    </>
  );
};

const MuiSubMenuWrapper = ({ menuItemLabel, subMenus }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { theme } = useTheme();
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getMenuLabelColor = theme => theme === "dark" ? "#ffffff" : "#00050B";

  return (
    <div>
      <div
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        style={{
          color: getMenuLabelColor(theme),
          display: "flex",
          justifyContent: "space-between",
          padding: "10px",
          width: "100%",
          cursor: "pointer"
        }}
      >
        <div>{menuItemLabel}</div>
        <KeyboardArrowRight fontSize="small" sx={{ display: "inline-block", verticalAlign: "middle", marginLeft: "10px" }} />
      </div>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button"
        }}
      >
        {subMenus.map(menu => {
          return (
            <div key={formatKey(menu.title)}>
              {menu.reportItem ? (
                <ReportItemSubMenuItem menu={menu} />
              ) : (
                <Link to={`${menu.link}`}>
                  <MenuItem key={formatKey(menu.title)} sx={{ width: "100%" }}>
                    {menu.title}
                  </MenuItem>
                </Link>
              )}
            </div>
          );
        })}
      </Menu>
    </div>
  );
};

const MuiMenuWrapper = ({ dropdownTitle, Icon, hasArrow, href, subMenus, active, onTabClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigator = useNavigate();
  const { theme } = useTheme();
  const handleClick = event => {
    if (hasArrow) {
      setAnchorEl(event.currentTarget);
    } else {
      onTabClick({ to: href });
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getIconColor = (active, open, theme) => {
    if (active || open) {
      return "#00B0EE";
    } else if (theme === "dark") {
      return "#ffffff";
    } else {
      return "#00050B";
    }
  };

  const iconColor = getIconColor(active, open, theme);

  return (
    <>
      <Button
        className={`tab ${active ? "active" : ""}`}
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        href={hasArrow ? "#" : href}
        sx={{
          color: iconColor,
          textTransform: "none",
          padding: "10px 16px",
          minWidth: "auto",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 176, 238, 0.1)"
          }
        }}
      >
        <div className="tab_main">
          <Icon color={iconColor} />
          <span className="menu-title">{dropdownTitle}</span>
          {hasArrow && <ExpandMore htmlColor={iconColor} />}
        </div>
      </Button>
      {hasArrow && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button"
          }}
        >
          {subMenus
            .filter(menu => {
              // Add any feature flag filtering here if needed
              return true;
            })
            .map(menu => (
              <div key={formatKey(menu.title)}>
                {menu.subMenus && menu.subMenus.length > 0 ? (
                  <MuiSubMenuWrapper menuItemLabel={menu.title} subMenus={menu.subMenus} />
                ) : (
                  <div>
                    {menu.link ? (
                      <Link reloadDocument={menu.reloadDocument} to={`${menu.link}`} target={menu.openInNewTab ? "_blank" : ""}>
                        <MenuItem sx={{ width: "100%" }}>{menu.title}</MenuItem>
                      </Link>
                    ) : (
                      <MenuItem sx={{ width: "100%" }} disabled>
                        {menu.title}
                      </MenuItem>
                    )}
                    {menu.hasBreak && <hr />}
                  </div>
                )}
              </div>
            ))}
        </Menu>
      )}
    </>
  );
};

const SubHeaderBody = ({ tabs, activeTabData, onTabClick }) => {
  return (
    <>
      {tabs.map(tab => (
        <div className="tab_main" key={formatKey(tab.title)}>
          <MuiMenuWrapper
            dropdownTitle={tab.title}
            Icon={tab.icon}
            hasArrow={tab.hasArrow}
            href={tab.hasArrow ? "#" : tab.to}
            subMenus={tab.subMenus}
            active={activeTabData?.title === tab.title}
            onTabClick={onTabClick}
          />
        </div>
      ))}
    </>
  );
};

const groupByCategory = arr => {
  return arr.reduce((acc, obj) => {
    const category = obj.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(obj);
    return acc;
  }, {});
};

const getReportMenus = () => {
  const { defaultReportRoutes, customReportRoutes } = useSelector(routerSelector);
  const groupedCustomReportRoutes = groupByCategory(customReportRoutes);
  const groupedDefaultReportRoutes = groupByCategory(defaultReportRoutes);
  const customSubMenus = []
  for (const [key, value] of Object.entries(groupedCustomReportRoutes)) {
    const subMenuTitle = key;

    const submenu = {
      hasArrow: false,
      ignorePermission: true,
      link: "rti",
      title: subMenuTitle,
      subMenus: value.map(sm => {
        return {
          fileName: sm.fileName,
          hasArrow: false,
          ignorePermission: true,
          link: sm.path,
          title: sm.title,
          reportItem: true,
          reportId: sm.reportId,
          filters: sm.filters,
        };
      })
    };
    customSubMenus.push(submenu);
  }
  if (customSubMenus.length <= 0) {
    customSubMenus.push({
      hasArrow: false,
      ignorePermission: true,
      title: "No custom reports created"
    });
  }
  const customReportsMenu = {
    to: "/custom",
    title: "Custom Reports",
    hasArrow: true,
    icon: NextWeek,
    subMenus: customSubMenus
  };

  const standardSubMenus = []
  standardSubMenus.push({
    link: "/reports-overview",
    title: "Edit/Schedule Reports",
    hasArrow: false,
    ignorePermission: true,
    reloadDocument: true
  });
  standardSubMenus.push({
    link: "/report-schedules",
    title: "View Report Schedules",
    hasArrow: false,
    ignorePermission: true,
    hasBreak: true,
    reloadDocument: true
  });
  for (const [key, value] of Object.entries(groupedDefaultReportRoutes)) {
    const subMenuTitle = key;
    const submenu = {
      hasArrow: false,
      ignorePermission: true,
      link: "rti",
      title: subMenuTitle,
      subMenus: value.map(sm => {
        return {
          fileName: sm.fileName,
          to: sm.path,
          hasArrow: false,
          ignorePermission: true,
          link: sm.path,
          title: sm.title,
          reportItem: true,
          reportId: sm.reportId,
          filters: sm.filters,
        };
      })
    };
    standardSubMenus.push(submenu);
  }

  const standardReportsMenu = {
    to: "/standard",
    title: "Standard Reports",
    hasArrow: true,
    icon: NextWeek,
    subMenus: standardSubMenus
  };

  return [standardReportsMenu, customReportsMenu];
}

const SubHeader = ({ tabs, onTabClick }) => {
  const location = useLocation();
  const activeTabData = useMemo(() => {
    return tabs.find(tab => location.pathname.startsWith(tab.to));
  }, [location.pathname, tabs]);

  const newTabs = [...tabs, ...getReportMenus()];

  return (
    <div className="subheader">
      <SubHeaderBody tabs={newTabs} activeTabData={activeTabData} onTabClick={onTabClick} />
    </div>
  );
};

// PropTypes for the new components
ReportItemSubMenuItem.propTypes = {
  menu: PropTypes.shape({
    title: PropTypes.string.isRequired,
    link: PropTypes.string,
    filters: PropTypes.object
  }).isRequired
};

MuiSubMenuWrapper.propTypes = {
  menuItemLabel: PropTypes.string.isRequired,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string,
      reportItem: PropTypes.bool,
      reportId: PropTypes.string,
      filters: PropTypes.object
    })
  ).isRequired
};

MuiMenuWrapper.propTypes = {
  dropdownTitle: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  hasArrow: PropTypes.bool,
  href: PropTypes.string,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string,
      reportItem: PropTypes.bool,
      reportId: PropTypes.string,
      filters: PropTypes.object,
      subMenus: PropTypes.array,
      reloadDocument: PropTypes.bool,
      openInNewTab: PropTypes.bool,
      hasBreak: PropTypes.bool
    })
  ),
  active: PropTypes.bool,
  onTabClick: PropTypes.func.isRequired
};

SubHeaderBody.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      hasArrow: PropTypes.bool,
      subMenus: PropTypes.array,
      to: PropTypes.string.isRequired
    })
  ).isRequired,
  activeTabData: PropTypes.object,
  onTabClick: PropTypes.func.isRequired
};

SubHeader.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      hasArrow: PropTypes.bool,
      subMenus: PropTypes.arrayOf(PropTypes.string),
      to: PropTypes.string.isRequired
    })
  ).isRequired,
  onTabClick: PropTypes.func.isRequired
};

export default SubHeader;
